import { DateTime } from 'luxon';
import { GenreViewModel } from '../genre/genre.view.model';
import { BannerViewModel } from '../banner.view.model';
import { TagGroupViewModel } from '../tag-group.view.model';
import { RatingViewModel } from '../rating.view.model';
import { EventItemViewModel } from './event-item.view.model';
import { TrailerViewModel } from '../movie/trailer.view.model';
import { AbstractViewModel } from '../../abstract.view.model';
import { EventApiModel } from '../../api-model/event/event.api.model';
import { MoviePrintViewModel } from '../movie/movie-print.view.model';

export class EventViewModel extends AbstractViewModel<EventApiModel> {
  id: string;
  duration: number;
  description: string;
  ageLongDescription: string;
  releaseDate: DateTime;
  distributorId: string;
  distributor: string;
  genres: GenreViewModel[];
  trailers: string[];
  posters: string[];
  pictures: string[];
  posPosters: string[];
  banners: BannerViewModel[];
  ratings: RatingViewModel[];
  name: string;
  link: string;
  screeningId: string;
  screenId: string;
  regionId: string;
  cinemaId: string;
  timeFrom: DateTime;
  timeTo: DateTime;
  saleTimeTo: DateTime;
  reservationTimeTo: DateTime;
  availabilityStatus: number;
  externalId: number;
  items: EventItemViewModel[];
  tagGroups: TagGroupViewModel[];
  release: string;
  priority: number;
  generalAdmission: boolean;
  audience: number;
  maxOccupancy: number;
  isScreenSwapping: boolean;

  likeByUser: boolean;
  likes: number;
  trailersGroupByLang: TrailerViewModel[] = [];
  screeningTimeFrom: DateTime;
  movieCopy: MoviePrintViewModel;
  promote_outside_schedule: number;
  randomPosterOrDefault: string;
  slug: string;

  constructor(protected apiModel: EventApiModel = new EventApiModel()) {
    super(apiModel);
    this.fromApiModel();
  }

  protected fromApiModel(): void {
    this.id = this.apiModel.id;
    this.duration = this.apiModel.duration;
    this.description = this.apiModel.description;
    this.distributorId = this.apiModel.distributorId;
    this.distributor = this.apiModel.distributor;
    this.genres = this.apiModel.genres ? this.apiModel.genres.map((genre) => new GenreViewModel(genre)) : [];
    this.trailers = this.apiModel.trailers ?? [];
    this.posters = this.apiModel.posters ?? [];
    this.pictures = this.apiModel.pictures ?? [];
    this.posPosters = this.apiModel.posPosters;
    this.banners = this.apiModel.banners ? this.apiModel.banners.map((banner) => new BannerViewModel(banner)) : [];
    this.ratings = this.apiModel.ratings ? this.apiModel.ratings.map((rating) => new RatingViewModel(rating)) : [];
    this.name = this.apiModel.name;
    this.link = this.apiModel.link;
    this.screeningId = this.apiModel.screeningId;
    this.screenId = this.apiModel.screenId;
    this.cinemaId = this.apiModel.cinemaId;
    this.screeningTimeFrom = this.timeFrom = this.apiModel.timeFrom ? DateTime.fromISO(this.apiModel.timeFrom, { setZone: true }) : null;
    this.timeTo = this.apiModel.timeTo ? DateTime.fromISO(this.apiModel.timeTo, { setZone: true }) : null;
    this.saleTimeTo = this.apiModel.saleTimeTo ? DateTime.fromISO(this.apiModel.saleTimeTo, { setZone: true }) : null;
    this.reservationTimeTo = this.apiModel.reservationTimeTo ? DateTime.fromISO(this.apiModel.reservationTimeTo, { setZone: true }) : null;
    this.availabilityStatus = this.apiModel.availabilityStatus;
    this.externalId = this.apiModel.externalId;
    this.items = this.apiModel.items ? this.apiModel.items.map((item) => new EventItemViewModel(item)) : [];
    this.tagGroups = this.apiModel.tagGroups ? this.apiModel.tagGroups.map((tagGroup) => new TagGroupViewModel(tagGroup)) : [];
    this.release = this.apiModel.release;
    this.generalAdmission = this.apiModel.generalAdmission;
    this.audience = this.apiModel.audience;
    this.maxOccupancy = this.apiModel.maxOccupancy;
  }

  toApiModel(): EventApiModel {
    return undefined;
  }

  get isEvent(): boolean {
    return true;
  }

  get title(): string {
    return this.name;
  }

  ageRestriction(fields: string[] = ['value'], cinemaGroupId?: string): string {
    if (!this.ratings || this.ratings.length === 0) {
      return '-';
    }

    if (!fields) {
      fields = ['value'];
    }

    const filteredRatings = this.ratings.filter((f) => !cinemaGroupId || f.cinemaGroupId === cinemaGroupId);

    const mappedValues = filteredRatings.map((rating) => {
      return fields
        .map((field) => rating[field])
        .filter((val) => val !== undefined && val !== null && val !== '')
        .join(' ');
    });

    return Array.from(new Set(mappedValues))
      .filter((val) => val.length > 0)
      .join(' / ');
  }

  get genreNames(): string {
    return this.genres.map((o) => o.name).join(', ');
  }

  get firstPosterOrDefault(): string {
    return this.posters?.length ? this.posters[0] : null;
  }

  tagsFlattenArray(includes?: string[]) {
    return this.tagGroups.flatMap((group) =>
      group.tags.filter((f) => includes?.length === 0 || includes.map((i) => i.toLowerCase()).includes(f.symbol.toLowerCase()))?.map((tag) => tag.symbol)
    );
  }
}
